import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { customerApi } from '@/portal/lib/api'
import { useMatrixCaps } from './tabs/matrix/MatrixCapabilities.tsx'
import { type Group, type GroupType, GROUP_TYPES } from './types.ts'

interface StandardItem {
  id: string
  type: string
  name_template: string
  description?: string | null
  config?: { groupType?: string; ou?: string } | null
}

export const GLOBAL_GROUP_ID_PREFIX = 'global:'

export function isGlobalGroupId(id: string): boolean {
  return id.startsWith(GLOBAL_GROUP_ID_PREFIX)
}

const VALID_TYPES = new Set<string>(GROUP_TYPES)

/**
 * Laedt die globalen Standard-Gruppen (aus /it-structures/global/items, type=group)
 * und mappt sie auf `Group`-Objekte mit stabilen, praefixierten IDs (`global:<uuid>`).
 *
 * Diese Gruppen werden in der UserGroupMatrix und DepartmentGroupMatrix zusaetzlich
 * zu den kundenspezifischen Gruppen angezeigt. Die Gruppen selbst sind in der
 * Kundenstruktur nicht persistiert — Mitgliedschaften (user.groupIds) referenzieren
 * die praefixierten IDs und werden in structure_data.users gespeichert.
 */
export function useGlobalStandardGroups(): Group[] {
  const caps = useMatrixCaps()
  const isCustomer = caps.variant === 'customer'
  const { data } = useQuery<{ items: StandardItem[] }>({
    queryKey: ['it-standard-items', caps.variant],
    queryFn: () =>
      isCustomer
        ? customerApi.get('/it-structures/global/items').then((r) => r.data)
        : apiClient.get('/it-structures/global/items').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  })

  return useMemo<Group[]>(() => {
    const items = data?.items ?? []
    return items
      .filter((i) => i.type === 'group')
      .map((i) => {
        const rawType = i.config?.groupType ?? 'FU'
        const type: GroupType = (VALID_TYPES.has(rawType) ? rawType : 'FU') as GroupType
        return {
          id: `${GLOBAL_GROUP_ID_PREFIX}${i.id}`,
          type,
          name: i.name_template,
          description: i.description ?? undefined,
        }
      })
  }, [data])
}
